import { useState, useEffect } from "react";

import { Grid, Divider, Typography } from "@mui/material";

import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { ToggleButtons } from "@aclymatepackages/atoms";
import { DEFAULT_TONS_CO2E_PER_MILE } from "@aclymatepackages/constants";
import { numbersRegExpTest } from "@aclymatepackages/reg-exp";
import { fuelCarbonFromGallons } from "@aclymatepackages/calcs/travel";

import FuelVolumeInput from "../FuelVolumeInput";
import DistanceInputOptions from "../DistanceInputOptions";

import {
  buildInitialTransactionInputValueFromSchema,
  findDistanceMiles,
  convertVolumeToGallons,
} from "../../../helpers/components/inputs";

const FuelVolumeInputRow = ({
  editInputData,
  fuelType,
  billGallons,
  fuelVolume,
  volumeUnit,
  disabled,
}) => (
  <Grid container direction="column" spacing={2} wrap="nowrap">
    <Grid item container justifyContent="center">
      <Grid item>
        <ToggleButtons
          size="small"
          value={fuelType}
          onChange={editInputData("fuelType")}
          buttons={[
            {
              value: "gas",
              name: "Gasoline",
              id: "gas",
            },
            {
              value: "diesel",
              name: "Diesel",
              id: "diesel",
            },
          ]}
          disabled={disabled}
        />
      </Grid>
    </Grid>
    <Grid item>
      <FuelVolumeInput
        disabled={disabled}
        billGallons={billGallons}
        fuelVolume={fuelVolume}
        volumeUnit={volumeUnit}
        editTransaction={editInputData}
      />
    </Grid>
  </Grid>
);

const MileageInput = ({
  inputData,
  editInputData,
  setCalcLoading,
  mileage,
  setCalculatedMileage,
  disabled,
}) => {
  const {
    purchaseType,
    fuelType,
    billGallons,
    fuelVolume,
    volumeUnit,
    unit,
    volume,
  } = inputData;

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <Grid container spacing={1} wrap="nowrap" alignItems="center">
          <Grid item>
            <Typography variant="subtitle2">
              Which type of vehicle was this fuel purchased for?
            </Typography>
          </Grid>
          <Grid item>
            <ToggleButtons
              size="small"
              value={purchaseType}
              onChange={editInputData("purchaseType")}
              buttons={[
                {
                  value: "company-vehicles",
                  name: "Company",
                  id: "company-vehicle-option",
                },
                {
                  value: "rental-cars",
                  name: "Rental",
                  id: "rental-vehicle-option",
                },
              ]}
              style={{ margin: "auto" }}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Divider />
      </Grid>
      <Grid item>
        <DistanceInputOptions
          type="mileage"
          inputData={inputData}
          editInputData={editInputData}
          setCalcLoading={setCalcLoading}
          oldDbMileageValue={mileage}
          setCalculatedMileage={setCalculatedMileage}
          startAdditionalRow={{
            RowComponent: FuelVolumeInputRow,
            props: {
              editInputData,
              fuelType,
              billGallons,
              fuelVolume: fuelVolume || volume,
              volumeUnit: volumeUnit || unit,
              disabled,
            },
            title: "Fuel Volume",
          }}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

export const useMileageInput = ({
  transaction,
  editTransaction,
  onSave,
  setCalcLoading,
}) => {
  const { vehicle, subcategory, status } = transaction;
  const { tonsCo2ePerMile = DEFAULT_TONS_CO2E_PER_MILE } = vehicle || {};

  const inputSchema = [
    { field: "purchaseType", defaultValue: "company-vehicles" },
    { field: "roundTrip", defaultValue: false },
    { field: "to" },
    { field: "from" },
    { field: "milesDriven" }, //This is to handle old "rental-cars" transactions that have been merged into this input
    { field: "mileage" }, //this is an old value from the old schema
    { field: "distance" },
    { field: "distanceUnit" },
    { field: "fuelCost" },
    { field: "gasStation" },
    { field: "billGallons" },
    { field: "fuelType", defaultValue: "gas" },
    { field: "fuelVolume" },
    { field: "volumeUnit" },
    { field: "volume" },
    { field: "unit" },
  ];

  const [calculatedMileage, setCalculatedMileage] = useState(0);
  const [inputData, setInputData] = useState(
    buildInitialTransactionInputValueFromSchema(transaction, inputSchema)
  );

  const editInputData = (field) => (value) =>
    editObjectData(setInputData, field, value);

  const {
    distance,
    roundTrip,
    mileage,
    to,
    from,
    fuelVolume,
    volumeUnit,
    billGallons,
    fuelType,
    milesDriven,
    purchaseType,
  } = inputData;

  useEffect(() => {
    if (subcategory === "company-vehicles" || subcategory === "rental-cars") {
      editTransaction("subcategory")(purchaseType);
    }
  }, [editTransaction, subcategory, purchaseType]);

  const calculateTonsCo2e = () => {
    const convertFuelVolumeToGallons = () => {
      if (fuelVolume && volumeUnit) {
        return convertVolumeToGallons(fuelVolume, volumeUnit);
      }

      return billGallons || 0;
    };

    if (fuelVolume && volumeUnit && fuelType) {
      return fuelCarbonFromGallons(convertFuelVolumeToGallons(), fuelType);
    }

    const distanceMiles =
      findDistanceMiles(inputData, calculatedMileage) || mileage;

    if (roundTrip && to && from) {
      return distanceMiles * tonsCo2ePerMile * 2;
    }

    return distanceMiles * tonsCo2ePerMile;
  };

  const tonsCo2e = calculateTonsCo2e();
  const onTransactionSave = () =>
    onSave({
      tonsCo2e,
      subcategory: purchaseType,
      emissionData: inputData,
    });
  const saveEnabled =
    (fuelVolume && numbersRegExpTest(fuelVolume) && volumeUnit) ||
    (!!distance && numbersRegExpTest(distance)) ||
    !!calculatedMileage;

  return {
    inputBlock: (
      <MileageInput
        inputData={inputData}
        editInputData={editInputData}
        mileage={mileage || milesDriven}
        setCalcLoading={setCalcLoading}
        setCalculatedMileage={setCalculatedMileage}
        disabled={status === "locked"}
      />
    ),
    saveEnabled,
    onTransactionSave,
    tonsCo2e,
  };
};
export default useMileageInput;
